import _ from "lodash";
import React, { useState, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { colors } from "Constants";
import { Trans } from "react-i18next";

import {
  Button,
  Modal,
  TextInput,
  TextAreaInput,
  EmptyState,
  Dialog,
  RadioButton,
} from "@toddle-edu/ds-web";
import { ImageSquareOutlined } from "@toddle-edu/ds-icons";
import { NoSearchInFilterIllustration } from "@toddle-edu/ds-theme";
import I18nHOC from "UIComponents/I18nHOC";
import ProfilePhotoPickerV2 from "UIComponents/ProfilePhotoPickerV2";
import DualPanelManageMembers from "AppComponents/DualPanelManageMembers";
import DualPanelManageMembersV2 from "AppComponents/DualPanelManageMembersV2";
import { DUAL_PANEL_VIEW_MODE } from "AppComponents/DualPanelManageMembersV2/module/utils";
import { compose } from "lib/apolloClient";
import {
  getGradeUsers,
  handleCreateChannel,
} from "MessagingV2/modules/MessagingModule";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import {
  getIsMessagingWidget,
  isMessagingRevempEnabled,
  isAddMembersV3Enabled,
  isPaidConnectModule,
  isMessagingCurriculumWiseSplitEnabled,
  isMessagingPublicChannelEnabled,
  getUserTypeOption,
  isDualPanelRevampEnabled,
  KEYBOARD_SHORTCUT,
} from "MessagingV2/modules/Utils";

import { getDualPanelQueryWrapperConfig } from "MessagingV2/components/DualPanelQueryWrapper";

import AddMembersSearch from "../AddMembers/components/AddMembersSearch";
import AddMembersSearchV2 from "../AddMembersV2/components/AddMembersSearch";

import ViewMembers from "../AddMembers/components/ViewMembers";
import ViewMembersV2 from "../AddMembersV2/components/ViewMembers";

import classes from "./CreateChannelModal.scss";
import classNames from "classnames";
import UsersAndCohortsQueryWrapper, {
  metadataProps,
} from "../AddMembersV3/UsersAndCohortsQueryWrapper";

import { getOrganizationId } from "modules/CommonSelectors";
import { getOrganizationDetailsFromCache } from "modules/CommonGraphqlHelpers";
import Filter, { FILTER_KEYS } from "./Filter";

const stepsConfig = {
  BASIC_DETAILS: "BASIC_DETAILS",
  ADD_MEMBERS: "ADD_MEMBERS",
};

const styles = {
  modalBodyStyle: {
    padding: "0",
    overflow: "hidden",
  },
  searchOverlayContainerStyleV2: {
    maxHeight: "320px",
  },

  searchOverlayContainerStyle: {
    maxHeight: "320px",
    width: "calc(100% - 32px)",
  },
  hasTagIconStyle: {
    fontWeight: "600",
    fontSize: "22px",
    color: colors.iconSubtle,
    fontFamily: "system-ui",
  },
  emptyStateSubTitleStyle: {
    width: "280px",
  },
  emptyStateSubTitleStyleV2: {
    width: "380px",
  },
  threadsContainerStyles: {
    height: "80%",
  },
  dropZoneStyle: {
    height: "auto",
    width: "160px",
  },
  modalHeaderStyle: {
    padding: "16px 24px",
    marginBottom: 0,
  },
  modalFooterStyle: {
    padding: "16px 24px",
    marginTop: "0px",
  },
};

const overrideClasses = {
  usersViewContainer: classes.usersViewContainer,
};

const CreateChannelModal = ({
  onCloseModal,
  t,
  handleCreateChannel,
  onConfirmClick,
}) => {
  const STEPS_LIST = [
    {
      id: stepsConfig.BASIC_DETAILS,
      buttonOneLabel: t("common:cancel"),
      buttonLabelPrimary: t("common:next"),
      height: "620px",
      width: "480px",
    },
    {
      ...(isAddMembersV3Enabled()
        ? {
            id: stepsConfig.ADD_MEMBERS,
            buttonOneLabel: t("common:back"),
            buttonLabelPrimary: t("common:create"),
            height: "680px",
            width: "1000px",
          }
        : {
            id: stepsConfig.ADD_MEMBERS,
            buttonOneLabel: t("common:back"),
            buttonLabelPrimary: t("common:create"),
            height: "675px",
            width: "600px",
          }),
    },
  ];

  const formDataInit = {
    label: "",
    description: "",
    profileImage: null,
    isPublic: true,
  };

  const [formData, setFormData] = useState(formDataInit);
  const [isUploading, setIsUploading] = useState(false);

  const [selectionStep, setSelectionStep] = useState(stepsConfig.BASIC_DETAILS);
  const [isPrimaryButtonDisable, setIsPrimaryButtonDisable] = useState(false);
  const [loadingState, setLoadingState] = useState({});
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [typeFilter, setTypeFilter] = useState(DUAL_PANEL_VIEW_MODE.MEMBERS);
  const [selectedFilters, setSelectedFilters] = useState({
    [FILTER_KEYS.SCHOOL]: [],
    [FILTER_KEYS.USER_TYPE]: [],
  });
  const { screenMode } = useChatOptions();

  const customQueryWrapperFields = useMemo(
    () => ({ typeFilter, selectedFilters }),
    [typeFilter, selectedFilters]
  );

  const { metadata, renderQueryWrapper } = useMemo(
    getDualPanelQueryWrapperConfig,
    []
  );

  const renderUsersAndCohortsQueryWrapper = useCallback(
    props => (
      <UsersAndCohortsQueryWrapper
        includePaidUsersOnly={isMessagingCurriculumWiseSplitEnabled()}
        {...props}
      />
    ),
    []
  );

  const isWidget = getIsMessagingWidget({ screenMode });

  const currentStep = _.find(STEPS_LIST, { id: selectionStep }, {});

  const selectedUserIds = _.map(selectedUsers, "id");

  const showAddMemberCountInFooter =
    !_.isEmpty(selectedUsers) & (selectionStep === stepsConfig.ADD_MEMBERS);

  const handleAddUser = ({ user }) => {
    setSelectedUsers(state => {
      return [...state, user];
    });
  };

  const handleRemoveUsers = users => {
    const userIds = _.map(users, "id");
    setSelectedUsers(state => {
      return _.filter(state, user => !_.includes(userIds, user.id));
    });
  };

  const handleAddUsers = users => {
    setSelectedUsers(state => {
      const userList = _.map(users, user => {
        return _.pick(user, ["id", "type"]);
      });
      const allUsers = [...state, ...userList];
      return _.uniqBy(allUsers, "id");
    });
  };

  const onConfirm = async () => {
    setIsPrimaryButtonDisable(true);
    const userIds = _.map(selectedUsers, "id");
    if (_.isFunction(onConfirmClick)) {
      onConfirmClick({
        label: formData.label,
        description: formData.description,
        userIds,
      });
    } else {
      const isPublicChannelEnable = isMessagingPublicChannelEnabled();
      handleCreateChannel({
        label: formData.label,
        description: formData.description,
        profileImage: formData.profileImage,
        isPublic: isPublicChannelEnable ? formData.isPublic : false,
        userIds,
        isWidget,
      });
    }
    onCloseModal();
    setIsPrimaryButtonDisable(false);
  };

  const handleButtonDisable = () => {
    switch (selectionStep) {
      case "BASIC_DETAILS":
        return _.isEmpty(formData.label) || isUploading;
      case "ADD_MEMBERS":
        return _.isEmpty(selectedUsers) || isPrimaryButtonDisable;

      default:
        return false;
    }
  };

  const getHeaderTitle = ({ t }) => {
    switch (selectionStep) {
      case "BASIC_DETAILS":
        return t("common:create_a_new_with_label", {
          label: t("common:channel"),
        });
      case "ADD_MEMBERS":
        return t("common:add_members_to_label", {
          label: _.get(formData, "label"),
        });
    }
  };

  const onPrimaryButtonClick = async () => {
    switch (selectionStep) {
      case stepsConfig.BASIC_DETAILS:
        return setSelectionStep(stepsConfig.ADD_MEMBERS);
      case stepsConfig.ADD_MEMBERS:
        return await onConfirm();
      default:
        return;
    }
  };

  const onSecondaryButtonClick = () => {
    switch (selectionStep) {
      case stepsConfig.BASIC_DETAILS:
        return onCloseModal();
      case stepsConfig.ADD_MEMBERS:
        return setSelectionStep(stepsConfig.BASIC_DETAILS);
    }
  };

  const handleProgressStep = ({ step }) => {
    setSelectionStep(step);
  };

  const updateUsers = value => {
    setSelectedUsers(value);
  };

  const onTitleTextChange = e => {
    setFormData(formData => ({ ...formData, label: e.target.value }));
  };

  const onDescriptionTextChange = e => {
    setFormData(formData => ({ ...formData, description: e.target.value }));
  };

  const updateProfilePicture = profileImage => {
    setFormData(formData => ({
      ...formData,
      profileImage,
    }));
  };

  const setPublicChannel = isPublic => {
    setFormData(formData => ({
      ...formData,
      isPublic,
    }));
  };

  const isUIRevampEnabled = isMessagingRevempEnabled();

  const handleAddGrade = async ({ gradeId, userType }) => {
    if (!gradeId || !userType) return;
    const loaderKey = `${userType}_${gradeId}`;
    if (loadingState[loaderKey]) return;
    setLoadingState(state => ({ ...state, [loaderKey]: true }));
    // api call to get user ids
    const userIds = await getGradeUsers({ gradeId, userType });

    if (!_.isEmpty(userIds)) handleAddUsers(userIds);
    setLoadingState(state => _.omit(state, loaderKey));
  };

  const isLoading = !_.isEmpty(loadingState);

  const loadingStateByType = useMemo(
    () =>
      _.reduce(
        loadingState,
        (result, value, key) => {
          const firstWord = key.split("_")[0];
          result[firstWord] = result[firstWord] || value;
          return result;
        },
        {}
      ),
    [loadingState]
  );

  const renderModalBody = () => {
    switch (selectionStep) {
      case stepsConfig.BASIC_DETAILS: {
        const isPublicChannelEnable = isMessagingPublicChannelEnabled();

        const orgId = getOrganizationId();
        const orgName = _.get(
          getOrganizationDetailsFromCache(orgId),
          "name",
          ""
        );
        return (
          <div className={classes.basicDetailContainer}>
            <div className={classes.profileImageContainer}>
              <ProfilePhotoPickerV2
                value={formData.profileImage}
                showButtonOnHover={true}
                uploadStatus={setIsUploading}
                icon={<ImageSquareOutlined />}
                updateInputField={updateProfilePicture}
                text={t("common:add_image")}
              />
            </div>
            <TextInput
              dsVersion="2.0"
              label={`${t("common:channel_name")}*`}
              onChange={onTitleTextChange}
              placeholder={t("common:channel_name_placeholder")}
              required
              value={formData.label}
            />
            <TextAreaInput
              dsVersion="2.0"
              label={t("common:description")}
              placeholder={t("common:channel_description_placeholder")}
              onChange={onDescriptionTextChange}
              value={formData.description}
              maxRows={2}
            />
            {isPublicChannelEnable ? (
              <div className={classes.publicChannelOption}>
                <div className="text-label">{t("common:visible_to")}</div>
                <div className={classes.radioButtonAndLabel}>
                  <RadioButton
                    dsVersion="2.0"
                    checked={formData.isPublic}
                    size="medium"
                    onChange={() => setPublicChannel(true)}
                  />
                  <div
                    className={classNames(
                      "text-body",
                      classes.publicChannelText
                    )}
                  >
                    <Trans i18nKey="common:public_channel_option_title">
                      <span className={classes.boldText} />
                      {{ orgName }}
                    </Trans>
                  </div>
                </div>
                <div className={classes.radioButtonAndLabel}>
                  <RadioButton
                    dsVersion="2.0"
                    checked={!formData.isPublic}
                    onChange={() => setPublicChannel(false)}
                    size="medium"
                  />
                  <div
                    className={classNames(
                      "text-body",
                      classes.publicChannelText
                    )}
                  >
                    {t("common:private_channel_option_title")}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        );
      }
      case stepsConfig.ADD_MEMBERS:
        if (isAddMembersV3Enabled()) {
          if (isDualPanelRevampEnabled()) {
            return (
              <DualPanelManageMembersV2
                handleAddUsers={handleAddUsers}
                handleRemoveUsers={handleRemoveUsers}
                metadataProps={metadata}
                renderQueryWrapper={renderQueryWrapper}
                overrideClasses={overrideClasses}
                filterComponent={
                  <Filter
                    typeFilter={typeFilter}
                    setTypeFilter={setTypeFilter}
                    selectedFilters={selectedFilters}
                    onFiltersChange={setSelectedFilters}
                  />
                }
                customQueryWrapperFields={customQueryWrapperFields}
              />
            );
          }

          return (
            <DualPanelManageMembers
              handleAddUsers={handleAddUsers}
              handleRemoveUsers={handleRemoveUsers}
              metadataProps={metadataProps}
              dsVersion="2.0"
              searchPlaceholder="common:search_member_placeholder"
              showTypeFilter
              typeFilterOptions={getUserTypeOption({ t })}
              renderQueryWrapper={renderUsersAndCohortsQueryWrapper}
              overrideClasses={overrideClasses}
            />
          );
        }

        return (
          <div className={classes.addMemberContainer}>
            {isUIRevampEnabled ? (
              <AddMembersSearchV2
                handleAddUsers={handleAddUsers}
                handleAddGrade={handleAddGrade}
                channelLabel={formData.label}
                excludedUserIds={selectedUserIds}
                includePaidUsersOnly={isPaidConnectModule()}
                overlayContainerStyle={styles.searchOverlayContainerStyleV2}
              />
            ) : (
              <AddMembersSearch
                handleAddUser={handleAddUser}
                channelLabel={formData.label}
                excludedUserIds={selectedUserIds}
                includePaidUsersOnly={isPaidConnectModule()}
                overlayContainerStyle={styles.searchOverlayContainerStyle}
              />
            )}
            {!_.isEmpty(selectedUsers) || isLoading ? (
              isUIRevampEnabled ? (
                <ViewMembersV2
                  users={selectedUsers}
                  updateUsers={updateUsers}
                  isAddMembers={true}
                  loadingStateByType={loadingStateByType}
                />
              ) : (
                <div className={classes.viewMembersContainer}>
                  <ViewMembers
                    users={selectedUsers}
                    updateUsers={updateUsers}
                    isAddMembers={true}
                    threadsContainerStyles={styles.threadsContainerStyles}
                  />
                </div>
              )
            ) : (
              <div className={classes.emptyState}>
                <EmptyState
                  illustration={NoSearchInFilterIllustration}
                  subtitle={
                    isUIRevampEnabled
                      ? t("common:search_for_member_grade_class_to_add")
                      : t("common:search_for_channel_members_to_add")
                  }
                  subtitleStyle={
                    isUIRevampEnabled
                      ? styles.emptyStateSubTitleStyleV2
                      : styles.emptyStateSubTitleStyle
                  }
                  size="small"
                />
              </div>
            )}
          </div>
        );
    }
  };

  const handleCloseModal = () => {
    switch (selectionStep) {
      case stepsConfig.BASIC_DETAILS:
        if (formData.profileImage || formData.label || formData.description) {
          return setShowDiscardModal(true);
        }
        return onCloseModal();
      case stepsConfig.ADD_MEMBERS:
        return setShowDiscardModal(true);
    }
  };

  const discardDialogMessage = useMemo(() => {
    switch (selectionStep) {
      case stepsConfig.BASIC_DETAILS:
        return t("common:basic_details_dialog_discard_message");
      case stepsConfig.ADD_MEMBERS:
        return t("common:add_members_dialog_discard_message");
    }
  }, [selectionStep, t]);

  const onCloseDialog = () => setShowDiscardModal(false);

  return (
    <>
      <Modal
        height={currentStep.height}
        width={currentStep.width}
        hasOverlay={true}
        shouldCloseOnEsc={true}
        shouldCloseOnOverlayClick={true}
        onClose={handleCloseModal}
        isOpen={true}
        dsVersion="2.0"
      >
        <Modal.Header
          style={styles.modalHeaderStyle}
          title={getHeaderTitle({ t })}
          isClosable={true}
          hasSeparator={true}
        />

        {/* <CustomProgressSteps
          shouldDisplayLabel={false}
          steps={STEPS_LIST}
          currentStep={selectionStep}
          borderWidth={4}
          onClick={
            _.isEmpty(formData.label) || isUploading
              ? _.noop
              : handleProgressStep
          }
        /> */}

        <Modal.Body style={styles.modalBodyStyle}>
          {renderModalBody()}
        </Modal.Body>
        <Modal.Footer hasSeparator={true} style={styles.modalFooterStyle}>
          <div className={classes.footerContainer}>
            {showAddMemberCountInFooter && !isAddMembersV3Enabled() ? (
              <div
                className={classNames(
                  "text-body-1",
                  classes.addMemberCountText
                )}
              >
                {t("common:label_selected", {
                  label: t("member_count_lowercase", {
                    count: _.size(selectedUsers),
                  }),
                })}
              </div>
            ) : null}
            <Button
              type="outlined"
              variant="neutral"
              size="medium"
              dsVersion="2.0"
              onClick={onSecondaryButtonClick}
            >
              {currentStep.buttonOneLabel}
            </Button>
            <Button
              variant="progressive"
              size="medium"
              dsVersion="2.0"
              onClick={onPrimaryButtonClick}
              disabled={handleButtonDisable()}
              shortcut={KEYBOARD_SHORTCUT.SHIFT_ENTER}
            >
              {currentStep.buttonLabelPrimary}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      <Dialog
        dsVersion="2.0"
        isOpen={showDiscardModal}
        onClose={onCloseDialog}
        variant={"delete-dialog"}
        title={t("common:are_you_sure")}
        message={discardDialogMessage}
        buttonOneText={t("common:cancel")}
        buttonTwoText={t("common:discard")}
        onButtonOneClick={onCloseDialog}
        onButtonTwoClick={onCloseModal}
      />
    </>
  );
};

const mapActionCreators = {
  handleCreateChannel,
};

export default compose(
  I18nHOC({ resource: ["common", "unitPlan"] }),
  connect(null, mapActionCreators)
)(CreateChannelModal);
